// covered-by: packages/schemas/src/christo/__tests__/askPunttOrgLabels.test.ts
import { z } from "zod";

export const ASK_PUNTT_ORG_LABEL_MAX_LEN = 40;
export const ASK_PUNTT_ORG_TAGS_MAX = 20;
export const ASK_PUNTT_ORG_REGIONS_MAX = 10;

/**
 * Trim, collapse internal whitespace, reject empty / over-long labels.
 * Returns null when the label is invalid.
 */
export function normalizeOrgLabel(raw: string): string | null {
  const collapsed = raw.trim().replace(/\s+/g, " ");
  if (!collapsed || collapsed.length > ASK_PUNTT_ORG_LABEL_MAX_LEN) {
    return null;
  }
  return collapsed;
}

/**
 * Normalize a label list: drop invalids, de-dupe case-insensitively
 * (first casing wins), cap at `max`. Null/undefined → [] (clear).
 */
export function normalizeOrgLabels(
  input: string[] | null | undefined,
  max: number,
): string[] {
  if (input == null) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    if (typeof raw !== "string") continue;
    const normalized = normalizeOrgLabel(raw);
    if (normalized == null) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
    if (out.length >= max) break;
  }
  return out;
}

export function normalizeOrgTags(input: string[] | null | undefined): string[] {
  return normalizeOrgLabels(input, ASK_PUNTT_ORG_TAGS_MAX);
}

export function normalizeOrgRegions(
  input: string[] | null | undefined,
): string[] {
  return normalizeOrgLabels(input, ASK_PUNTT_ORG_REGIONS_MAX);
}

/** Body / PATCH regions — empty array clears. */
export const askPunttOrgRegionsSchema = z
  .array(z.string())
  .transform((v) => normalizeOrgRegions(v));

/** Body / PATCH tags — empty array clears. */
export const askPunttOrgTagsSchema = z
  .array(z.string())
  .transform((v) => normalizeOrgTags(v));

/**
 * Coerce a query value into raw label strings.
 * why: TanStack JSON-serializes string[] search (`tags=["slug"]`); treating
 * that as one comma-string makes the API look up a literal `["slug"]` label.
 */
export function coerceOrgLabelQueryToArray(raw: unknown): string[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    return raw.filter((item): item is string => typeof item === "string");
  }
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item): item is string => typeof item === "string",
        );
      }
    } catch {
      // fall through to comma-split
    }
  }
  return trimmed.split(",").map((s) => s.trim());
}

/**
 * Query-param helper: accepts comma-separated string, JSON array string, or
 * repeated arrays. Empty → undefined (no filter).
 */
export function orgLabelQueryParamSchema(max: number) {
  return z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      if (v == null) return undefined;
      const arr = coerceOrgLabelQueryToArray(v);
      if (arr == null) return undefined;
      const normalized = normalizeOrgLabels(arr, max);
      return normalized.length > 0 ? normalized : undefined;
    });
}

export const askPunttOrgRegionsQuerySchema = orgLabelQueryParamSchema(
  ASK_PUNTT_ORG_REGIONS_MAX,
);
export const askPunttOrgTagsQuerySchema = orgLabelQueryParamSchema(
  ASK_PUNTT_ORG_TAGS_MAX,
);

export type AskPunttListSortBy =
  | "updatedAt"
  | "createdAt"
  | "uploadedAt"
  | "name";

export const askPunttListSortBySchema = z
  .enum(["updatedAt", "createdAt", "uploadedAt", "name"])
  .optional();
